"use client";

import { useEffect, useMemo, useState } from "react";
import { useStudio } from "@/contexts/studio-context";
import type {
  JourneySnapshot,
  LearningJourney,
} from "@/lib/learning-journey/journey";
import type { Phrase } from "@/lib/learning-journey/types";
import { SHELL_INSET_PX } from "./panel-metrics";
import { useShellOccupancy } from "./shell-occupancy";

/**
 * Phrase indicator chip, top-left of the piano-roll.
 *
 * Base state shows `Phrase X/Y` for the phrase currently in the viewport.
 * The actions it folds in depend on what is on screen behind it — it is the
 * one surface that always knows which passage the roll is on, so it is where
 * moving between passages lives, rather than a banner floating above the
 * roll or a list of units in a panel (38 lines in the median piece).
 *
 * **While a unit's parcours is on screen** and the player has scrolled away
 * from it:
 *
 *  - "Reprendre"        → snap the viewport back, no state change.
 *  - "M'entraîner ici"  → open the viewport unit's parcours, which resumes
 *                         wherever it was left.
 *
 * **On the piece screen** — phase 2 or the repertoire, where the parcours is
 * behind the player — the chip is the way back into it: "M'entraîner ici"
 * re-opens the parcours of whatever unit is in view, as a *visit*
 * (`revisitUnit`), and "Revenir au morceau" closes it. Without this, a piece
 * that has been through phase 1 has no route back to bar 3: the phase never
 * moves backwards, by design, and everything the parcours holds — the five
 * steps, the chaining, the stepper — became unreachable the moment the last
 * unit was validated.
 *
 * The viewport tick is polled at 4 Hz (250 ms); the studio updates it at
 * 60 Hz, so a coarse poll is enough for "which phrase is in view".
 *
 * ## Which corner it sits in
 *
 * Top-left, in a row of its own. The step panel's capsule is centred and
 * starts below that row (`PHRASE_CHIP_ROW_PX`), so the two never meet
 * whatever the roll's width or the capsule's height — a corner the chip
 * took on its own always ended up under the capsule, and « M'entraîner
 * ici », the only way into a unit from here, went with it.
 *
 * The chip still reads what the panel publishes (`shell-occupancy`) for the
 * one case where it must step aside entirely: the band, which spans the top
 * of the roll and carries the phrase label itself.
 */

const POLL_INTERVAL_MS = 250;

type PhraseChipProps = {
  readonly snapshot: JourneySnapshot;
  readonly journey: LearningJourney;
};

export function PhraseChip({ snapshot, journey }: PhraseChipProps) {
  const { studio } = useStudio();
  const occupancy = useShellOccupancy();
  const [viewportPhraseIdx, setViewportPhraseIdx] = useState<number | null>(
    null,
  );

  const phrases = useMemo(() => journey.getPhrases(), [journey]);
  const total = snapshot.phraseProgress.length;

  useEffect(() => {
    if (!studio) {
      return;
    }

    if (phrases.length === 0) {
      return;
    }

    const tick = (): void => {
      const t = studio.getEffectiveTick();
      setViewportPhraseIdx(findPhraseIndexAtTick(phrases, t));
    };

    // Initial read so the chip is correct on mount, then poll.
    tick();
    const id = setInterval(tick, POLL_INTERVAL_MS);

    return () => {
      clearInterval(id);
    };
  }, [studio, phrases]);

  // A unit's parcours is on screen — phase 1, or a visit opened from the
  // piece screen. Both navigate the same way.
  const training = snapshot.currentPhrase !== null;
  const visiting = snapshot.revisitingUnit !== null;

  // Mismatch = the player scrolled away from the unit the parcours is on.
  const mismatch =
    training &&
    viewportPhraseIdx !== null &&
    viewportPhraseIdx !== snapshot.currentPhraseIndex;

  // On the piece screen there is no unit to be away from: whatever is in
  // view is what the chip offers to work on.
  const offerTrain = mismatch || (!training && viewportPhraseIdx !== null);

  const displayedIdx =
    training && !mismatch
      ? snapshot.currentPhraseIndex
      : (viewportPhraseIdx ?? snapshot.currentPhraseIndex);
  const human = Math.min(displayedIdx + 1, Math.max(total, 1));

  const handleResume = (): void => {
    if (!studio) {
      return;
    }

    const fsmPhrase = snapshot.currentPhrase;

    if (!fsmPhrase) {
      return;
    }

    void studio.animateScrollToTick(fsmPhrase.startTick);
  };

  const handleBackToPiece = (): void => {
    journey.endRevisit();
  };

  const handleTrainHere = (): void => {
    if (viewportPhraseIdx === null) {
      return;
    }

    journey.revisitUnit(viewportPhraseIdx);

    if (!studio) {
      return;
    }

    const target = phrases[viewportPhraseIdx];

    if (!target) {
      return;
    }

    // Wait two frames so the new state component has mounted and fired
    // its own auto-scroll, then retarget the spring back to the phrase
    // start. The single ViewNavigator spring accepts retargeting, so the
    // user sees one continuous animation landing at the phrase start.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        void studio.animateScrollToTick(target.startTick);
      });
    });
  };

  // A band spans the top of the roll, which is where this chip lives, and it
  // carries the phrase label itself. Two of them side by side would be the
  // same fact twice, in two boxes.
  if (occupancy?.kind === "band") {
    return null;
  }

  return (
    <div
      className="pointer-events-auto absolute inline-flex items-center font-mono"
      style={{
        top: SHELL_INSET_PX,
        left: SHELL_INSET_PX,
        zIndex: 20,
        gap: 10,
        padding: "6px 10px",
        fontSize: 11,
        lineHeight: 1.2,
        color: "var(--color-text-secondary)",
        background: "rgba(20, 20, 26, 0.85)",
        border: "1px solid var(--color-border-subtle)",
        borderRadius: 8,
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
      }}
    >
      <span>
        Phrase {human}/{total}
      </span>

      {(offerTrain || visiting) && (
        <>
          <span
            aria-hidden
            style={{
              width: 1,
              height: 12,
              background: "var(--color-border-subtle)",
            }}
          />

          {/*
            Snapping the viewport back is offered while the parcours drives
            the screen. During a visit it is not: the visit already has one
            way out, and a chip carrying three acts on an iPad is a row of
            targets too small to aim at.
          */}
          {mismatch && !visiting && (
            <ChipAction
              onClick={handleResume}
              title={`Revenir à la phrase ${snapshot.currentPhraseIndex + 1}`}
            >
              Reprendre
            </ChipAction>
          )}

          {offerTrain && (
            <ChipAction
              onClick={handleTrainHere}
              title={`M'entraîner sur la phrase ${human}`}
              accent
            >
              M&apos;entraîner ici
            </ChipAction>
          )}

          {/*
            Always offered while a visit is open, including when the roll has
            wandered off the unit: a way out that disappears when you scroll
            is not a way out.
          */}
          {visiting && (
            <ChipAction onClick={handleBackToPiece} title="Revenir au morceau">
              Revenir au morceau
            </ChipAction>
          )}
        </>
      )}
    </div>
  );
}

function ChipAction({
  children,
  onClick,
  title,
  accent = false,
}: {
  children: React.ReactNode;
  onClick: () => void;
  title: string;
  accent?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="transition-colors focus:outline-none focus-visible:ring-2"
      title={title}
      style={{
        fontWeight: 500,
        color: accent
          ? "var(--color-violet-text)"
          : "var(--color-text-secondary)",
        background: "transparent",
        border: "none",
        // A finger, not a pointer: the row sits at 11 px and the hit area
        // has to come from the padding.
        padding: "6px 8px",
        margin: "-4px 0",
        borderRadius: 6,
        cursor: "pointer",
        whiteSpace: "nowrap",
        ["--tw-ring-color" as string]: "var(--color-accent)",
      }}
    >
      {children}
    </button>
  );
}

/**
 * Return the index of the phrase whose `[startTick, endTick)` contains
 * `tick`, or the closest one (last phrase if past the end, first if
 * before). `phrases` is assumed sorted by `startTick`.
 */
function findPhraseIndexAtTick(
  phrases: readonly Phrase[],
  tick: number,
): number {
  if (phrases.length === 0) {
    return -1;
  }

  if (tick < phrases[0].startTick) {
    return 0;
  }

  // Walk forward, phrases is small (typically < 50) so linear is fine
  // and avoids the off-by-one fiddliness of a binary search across a
  // half-open interval.
  for (let i = 0; i < phrases.length; i += 1) {
    const p = phrases[i];

    if (tick < p.endTick) {
      return i;
    }
  }

  return phrases.length - 1;
}
